import { prescriptionIdentifier } from "@/utils/prescriptionIdentity.js";
import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import Button from "@/components/ui/Button.jsx";
import PharmacyDesktopHeader from "@/components/nav/PharmacyDesktopHeader.jsx";
import ContextFeedbackLauncher from "@/components/feedback/ContextFeedbackLauncher.jsx";
import PharmacyFolaSmartCard from "@/pages/pharmacy/PharmacyFolaSmartCard.jsx";
import { useLanguage } from "@/contexts/LanguageContext.jsx";
import { useAuth } from "@/contexts/AuthContext.jsx";
import TopBar from "@/sot/primitives/TopBar.jsx";
import supabase from "@/services/supabaseClient.js";
import {
  PHARMACY_NOTIFICATIONS_CHANGED_EVENT,
  PHARMACY_NOTIFICATIONS_STORAGE_KEY,
  PHARMACY_PROFILE_CHANGED_EVENT,
  PHARMACY_PROFILE_STORAGE_KEY,
  readPharmacyNotificationsForActor,
  readPharmacyProfileForActor,
} from "@/services/pharmacyLaneService.js";
import { resolvePaymentCurrency } from "@/services/marketPaymentService.js";
import { makeBrandedQrDataUrl } from "@/utils/brandedQr.js";

const QR_ELEPHANT = "/afrimed_elephant_icon.svg";
const SURFACE_CLASS =
  "rounded-[12px] bg-[rgb(var(--clr-surface-cont-highest))] shadow-[var(--elev-1)]";
const MUTED_TEXT = "text-[rgb(var(--clr-on-surface-variant))]";
const PRIMARY_TEXT = "text-[rgb(var(--clr-on-surface))]";

function abs(path) {
  if (typeof window === "undefined") return String(path || "").trim();
  const clean = String(path || "").trim();
  return /^(?:https?:|data:|blob:)/i.test(clean) ? clean : `${window.location.origin}${clean}`;
}

function pickFirst(...values) {
  for (const value of values) {
    const text = String(value || "").trim();
    if (text) return text;
  }
  return "";
}

function formatMoney(value, currency = "ZAR", locale = "en-CH", pendingLabel = "Pending price") {
  const amount = Number(value || 0);
  if (!Number.isFinite(amount)) return pendingLabel;
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

function formatDateTime(value, locale = undefined, fallback = "—") {
  const date = new Date(value || "");
  if (Number.isNaN(date.getTime())) return fallback;
  return new Intl.DateTimeFormat(locale, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function getStatusLabel(status, t) {
  const value = String(status || "").trim().toLowerCase();
  if (value === "pharmacy_selected") return t("pharmacy.order.status.selected", "Pharmacy selected");
  if (value === "preparing") return t("pharmacy.order.status.preparing", "Preparing");
  if (value === "awaiting_payment") return t("pharmacy.order.status.awaitingPayment", "Awaiting payment");
  if (value === "paid") return t("pharmacy.order.status.paid", "Paid");
  if (value === "ready_for_pickup") return t("pharmacy.order.status.readyForPickup", "Ready for pickup");
  if (value === "rejected") return t("pharmacy.order.status.rejected", "Rejected");
  return t("pharmacy.order.status.issued", "Issued");
}

function getStatusTone(status) {
  const value = String(status || "").trim().toLowerCase();
  if (value === "preparing" || value === "awaiting_payment" || value === "paid" || value === "ready_for_pickup") {
    return "bg-[rgba(0,189,196,0.12)] text-[#008A8F]";
  }
  if (value === "rejected") {
    return "bg-[rgba(var(--clr-error),0.10)] text-[rgb(var(--clr-error))]";
  }
  return "bg-[rgba(var(--clr-on-surface-variant),0.12)] text-[rgb(var(--clr-on-surface-variant))]";
}

function DetailCard({ label, value, helper = "" }) {
  return (
    <div className="rounded-[12px] bg-[rgb(var(--clr-surface-container))] px-4 py-4">
      <div className={`text-label-large ${MUTED_TEXT}`}>{label}</div>
      <div className={`mt-2 text-title-medium ${PRIMARY_TEXT}`}>{value}</div>
      {helper ? <div className={`mt-1 text-body-small ${MUTED_TEXT}`}>{helper}</div> : null}
    </div>
  );
}

export default function PharmacyOrderDetails() {
  const navigate = useNavigate();
  const { prescriptionId } = useParams();
  const { t, languageCode } = useLanguage();
  const auth = useAuth() || {};
  const actor = { userId: auth?.user?.id, email: auth?.user?.email };
  const [profile, setProfile] = React.useState(() => readPharmacyProfileForActor(actor));
  const [notifications, setNotifications] = React.useState(() => readPharmacyNotificationsForActor(actor));
  const [view, setView] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [qrSrc, setQrSrc] = React.useState("");


  React.useEffect(() => {
    const refreshProfile = () => setProfile(readPharmacyProfileForActor(actor));
    const refreshNotifications = () => setNotifications(readPharmacyNotificationsForActor(actor));
    refreshProfile();
    refreshNotifications();
    const handleStorage = (event) => {
      if (!event?.key || event.key === PHARMACY_PROFILE_STORAGE_KEY) refreshProfile();
      if (!event?.key || event.key === PHARMACY_NOTIFICATIONS_STORAGE_KEY) refreshNotifications();
    };

    window.addEventListener(PHARMACY_PROFILE_CHANGED_EVENT, refreshProfile);
    window.addEventListener(PHARMACY_NOTIFICATIONS_CHANGED_EVENT, refreshNotifications);
    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener(PHARMACY_PROFILE_CHANGED_EVENT, refreshProfile);
      window.removeEventListener(PHARMACY_NOTIFICATIONS_CHANGED_EVENT, refreshNotifications);
      window.removeEventListener("storage", handleStorage);
    };
  }, [actor.userId, actor.email]);

  React.useEffect(() => {
    let cancelled = false;

    async function hydrate() {
      setLoading(true);
      setError("");

      try {
        const cleanPrescriptionId = String(prescriptionId || "").trim();
        if (!cleanPrescriptionId) {
          throw new Error(t("pharmacy.order.errors.missingId", "Prescription id is missing."));
        }

        const { data: fulfillmentRow, error: fulfillmentError } = await supabase
          .from("prescription_fulfillments")
          .select(`
            id,
            prescription_id,
            pharmacy_id,
            conversation_id,
            status,
            price,
            currency,
            created_at,
            updated_at,
            prescription:prescription_id (
              id,
              external_rx_id,
              issued_at,
              created_at,
              patient_user_id,
              meta
            )
          `)
          .eq("prescription_id", cleanPrescriptionId)
          .order("updated_at", { ascending: false })
          .order("created_at", { ascending: false })
          .limit(1)
          .maybeSingle();

        if (fulfillmentError) throw fulfillmentError;
        if (!fulfillmentRow?.id) {
          throw new Error(t("pharmacy.order.errors.loadFailed", "Could not load this pharmacy order."));
        }

        const prescription = Array.isArray(fulfillmentRow.prescription)
          ? fulfillmentRow.prescription[0] || {}
          : fulfillmentRow.prescription || {};
        const meta =
          prescription?.meta && typeof prescription.meta === "object" ? prescription.meta : {};

        const [{ data: pharmacyProfile }, { data: patientIdentity, error: identityError }] =
          await Promise.all([
            supabase
              .from("pharmacy_connect_profiles")
              .select("user_id, pharmacy_name, trading_name, country")
              .eq("user_id", String(fulfillmentRow.pharmacy_id || "").trim())
              .maybeSingle(),
            supabase.rpc("afrimed_pharmacy_assigned_patient_identity", {
              p_prescription_ids: [cleanPrescriptionId],
            }),
          ]);
        if (identityError) throw identityError;
        const assignedPatient = (Array.isArray(patientIdentity) ? patientIdentity : [])
          .find((row) => String(row?.prescription_id || "").trim() === cleanPrescriptionId);

        const currency = resolvePaymentCurrency({
          explicitCurrency: fulfillmentRow.currency || meta.currency,
          country: pickFirst(pharmacyProfile?.country, profile?.country, meta.patientCountry),
          fallbackCurrency: "ZAR",
        });

        const nextView = {
          prescriptionId: String(prescription?.id || cleanPrescriptionId).trim(),
          orderId: prescriptionIdentifier(prescription),
          medicineName: pickFirst(
            meta.medicineName,
            meta.medicine,
            meta.name,
            t("pharmacy.order.defaults.prescriptionItem", "Prescription item"),
          ),
          instructions: pickFirst(
            meta.dosageLabel,
            meta.frequencyLabel,
            meta.notes,
            t(
              "pharmacy.order.defaults.instructions",
              "Review the prescription instructions attached to this order.",
            ),
          ),
          medicationList: Array.isArray(meta.medications) ? meta.medications : [],
          patientName: pickFirst(
            assignedPatient?.patient_name,
            meta.patientName,
            t("pharmacy.order.patient", "Patient"),
          ),
          careSubjectType: pickFirst(meta.careSubjectType, "self").toLowerCase(),
          guardianName: pickFirst(meta.guardianName),
          responsibleRelationship: pickFirst(
            meta.responsibleRelationship,
            meta.care_subject_relationship,
          ),
          pharmacyName: pickFirst(
            pharmacyProfile?.pharmacy_name,
            pharmacyProfile?.trading_name,
            meta.requested_pharmacy_branch_name,
            meta.requested_pharmacy_name,
            t("pharmacy.order.defaults.pharmacy", "AfriMed pharmacy"),
          ),
          mode: String(
            meta.requested_fulfillment_mode || meta.fulfillment_mode || "pickup",
          ).trim().toLowerCase(),
          status: String(fulfillmentRow.status || "issued").trim().toLowerCase(),
          price: Number(fulfillmentRow.price || 0),
          currency,
          createdAt: pickFirst(fulfillmentRow.created_at, prescription?.issued_at, prescription?.created_at),
          updatedAt: pickFirst(fulfillmentRow.updated_at, fulfillmentRow.created_at),
        };

        if (!cancelled) {
          setView(nextView);
        }
      } catch (nextError) {
        if (!cancelled) {
          setError(nextError?.message || t("pharmacy.order.errors.loadFailed", "Could not load this pharmacy order."));
          setView(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    hydrate();
    return () => {
      cancelled = true;
    };
  }, [languageCode, prescriptionId, profile?.country, t]);

  React.useEffect(() => {
    let cancelled = false;

    if (typeof window === "undefined" || !view?.prescriptionId) {
      setQrSrc("");
      return undefined;
    }

    const qrValue = `${window.location.origin}/pharmacy/orders/${view.prescriptionId}`;
    makeBrandedQrDataUrl(qrValue, {
      iconSrc: abs(QR_ELEPHANT),
    })
      .then((value) => {
        if (!cancelled) setQrSrc(value);
      })
      .catch(() => {
        if (!cancelled) setQrSrc("");
      });

    return () => {
      cancelled = true;
    };
  }, [view?.prescriptionId]);

  function doDownloadPdf() {
    navigate(`/appointments/prescriptions/${view.prescriptionId}`);
  }

  return (
    <div className="min-h-dvh bg-[rgb(var(--clr-bg))] text-[rgb(var(--clr-on-surface))]">
      <PharmacyDesktopHeader section="prescriptions" notifications={notifications} />

      <main className="afrimed-rail afrimed-rail--comfortable [--rail-pad:12px] pt-4 pb-12 lg:[--rail-pad:var(--afrimed-rail-gutter-desktop)]">
        <TopBar
          title={t("pharmacy.order.title", "Prescription order")}
          subtitle={t(
            "pharmacy.order.subtitle",
            "Review the issued prescription, confirm the branch handoff, and use the queue to accept, reject, or set a price.",
          )}
          onBack={() => navigate("/pharmacy/prescriptions")}
          titleWrapperClassName="mt-2 mb-6"
        />

        {loading ? (
          <div className={`${SURFACE_CLASS} p-5`}>
            <div className="animate-pulse space-y-3">
              <div className="h-5 w-40 rounded bg-[rgba(var(--clr-on-surface),0.08)]" />
              <div className="h-4 w-64 rounded bg-[rgba(var(--clr-on-surface),0.06)]" />
              <div className="h-24 w-full rounded bg-[rgba(var(--clr-on-surface),0.06)]" />
            </div>
          </div>
        ) : error ? (
          <div className={`${SURFACE_CLASS} p-5`}>
            <div className="text-title-small text-[rgb(var(--clr-error))]">
              {error}
            </div>
            <div className="mt-4">
              <Button variant="tonal" onClick={() => navigate("/pharmacy/prescriptions")}>
                {t("pharmacy.order.backToQueue", "Back to prescription queue")}
              </Button>
            </div>
          </div>
        ) : view ? (
          <div className="space-y-6">
            <PharmacyFolaSmartCard
              title="Review this prescription order"
              body="Check the patient, medication, quoted price, fulfilment mode, and order status before taking action. If anything looks unclear, contact the issuing clinician or use the order actions available on this page."
              cta="Ask Fola about this order"
              route="/pharmacy/messages/fola"
              promptKey="prescription_order"
              returnTo={`/pharmacy/orders/${view.prescriptionId}`}
            />

            <section className={`${SURFACE_CLASS} p-6`}>
              <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                <div>
                  <div className={`text-label-large ${MUTED_TEXT}`}>{t("pharmacy.order.rxId", "RxID")}</div>
                  <div className={`mt-1 text-headline-small ${PRIMARY_TEXT}`}>{view.orderId}</div>
                  <div className={`mt-2 text-body-medium ${MUTED_TEXT}`}>
                    {view.pharmacyName} • {view.mode === "delivery" ? t("pharmacy.common.delivery", "Delivery") : t("pharmacy.common.pickup", "Pickup")}
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <span
                    className={`inline-flex rounded-full px-3 py-1 text-label-small ${getStatusTone(view.status)}`}
                  >
                    {getStatusLabel(view.status, t)}
                  </span>
                  <ContextFeedbackLauncher
                    section="pharmacy-order"
                    roleOverride="pharmacy"
                    entity={{
                      id: view.prescriptionId,
                      label: view.orderId || view.medicineName || t("pharmacy.order.title", "Prescription order"),
                      type: "pharmacy-order",
                    }}
                    context={{
                      status: view.status,
                      pharmacyName: view.pharmacyName,
                      mode: view.mode,
                    }}
                    subtitle={t(
                      "pharmacy.order.feedbackSubtitle",
                      "Capture this prescription order step so AfriMed can reopen the exact branch handoff context.",
                    )}
                  />
                </div>
              </div>

              <div className="mt-6 grid gap-4 lg:grid-cols-3">
                <DetailCard
                  label={t("pharmacy.order.patient", "Patient")}
                  value={view.patientName}
                  helper={
                    view.careSubjectType === "dependant" && view.guardianName
                      ? t("pharmacy.order.patientHelperWithGuardian", "Issued {date} • Responsible adult: {guardian}", {
                          date: formatDateTime(view.createdAt, languageCode),
                          guardian: `${view.guardianName}${
                            view.responsibleRelationship
                              ? ` • ${view.responsibleRelationship}`
                              : ""
                          }`,
                        })
                      : t("pharmacy.order.issuedAt", "Issued {date}", {
                          date: formatDateTime(view.createdAt, languageCode),
                        })
                  }
                />
                <DetailCard
                  label={t("pharmacy.order.medication", "Medication")}
                  value={view.medicineName}
                  helper={view.instructions}
                />
                <DetailCard
                  label={t("pharmacy.order.quotedPrice", "Quoted price")}
                  value={
                    view.price > 0
                      ? formatMoney(
                          view.price,
                          view.currency,
                          languageCode,
                          t("pharmacy.order.pendingPrice", "Pending price"),
                        )
                      : t("pharmacy.order.pendingPrice", "Pending price")
                  }
                  helper={
                    view.price > 0
                      ? t("pharmacy.order.updatedAt", "Updated {date}", {
                        date: formatDateTime(view.updatedAt, languageCode),
                      })
                      : t("pharmacy.order.pendingPriceHelper", "Set the price from the pharmacy queue when stock is confirmed.")
                  }
                />
              </div>
            </section>

            <section className={`${SURFACE_CLASS} p-6`}>
              <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_280px]">
                <div>
                  <h2 className={`text-title-large ${PRIMARY_TEXT}`}>
                    {t("pharmacy.order.medicationList", "Medication list")}
                  </h2>
                  {view.medicationList.length ? (
                    <div className="mt-4 space-y-3">
                      {view.medicationList.map((item, index) => {
                        const name = pickFirst(
                          item?.name,
                          item?.medicineName,
                          item?.label,
                          t("pharmacy.order.medicationItem", "Medication {index}", {
                            index: index + 1,
                          }),
                        );
                        const note = pickFirst(
                          item?.instructions,
                          item?.dosageLabel,
                          item?.strength,
                          item?.quantity,
                        );
                        return (
                          <div
                            key={`${name}-${index}`}
                            className="rounded-[12px] bg-[rgb(var(--clr-surface-container))] px-4 py-4"
                          >
                            <div className={`text-title-small ${PRIMARY_TEXT}`}>{name}</div>
                            {note ? (
                              <div className={`mt-1 text-body-medium ${MUTED_TEXT}`}>{note}</div>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className={`mt-4 rounded-[12px] bg-[rgb(var(--clr-surface-container))] px-4 py-4 text-body-medium ${MUTED_TEXT}`}>
                      {view.instructions}
                    </div>
                  )}

                  <div className="mt-5 flex flex-wrap gap-3">
                    <Button variant="tonal" onClick={doDownloadPdf}>
                      {t("prescriptions.common.openDocument", "Open E-Prescription PDF")}
                    </Button>
                  </div>
                </div>

                <div className="rounded-[12px] bg-[rgb(var(--clr-surface-container))] px-4 py-4">
                  <div className={`text-title-small ${PRIMARY_TEXT}`}>
                    {t("pharmacy.order.qr.title", "QR")}
                  </div>
                  <div className={`mt-1 text-body-small ${MUTED_TEXT}`}>
                    {t(
                      "pharmacy.order.qr.copy",
                      "Pass 1 uses the simple AfriMed order route for verification.",
                    )}
                  </div>
                  <div className="mt-4 flex items-center justify-center rounded-[12px] bg-white p-4">
                    {qrSrc ? (
                      <img
                        src={qrSrc}
                        alt={t("pharmacy.order.qr.alt", "QR for {id}", { id: view.orderId })}
                        className="h-[200px] w-[200px] object-contain"
                      />
                    ) : (
                      <div className={`text-body-small ${MUTED_TEXT}`}>
                        {t("pharmacy.order.qr.unavailable", "QR unavailable")}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </section>


          </div>
        ) : null}
      </main>
    </div>
  );
}
